import { Fragment, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FiFileText } from "react-icons/fi";
import { MessageRenderer, ActionLinks, PdfPanelContext } from "../../../../components/MessageWithTableRenderer";
import { AttachmentError, FileThumb, openAttachment } from "./AttachmentPanel";
import WebSourceCards, { hasPreview } from "./WebSourceCards";

// Same rule MessageBubble uses for every other answer: `table:` / `viz:` is the in-house syntax, which only
// MessageRenderer understands; anything else is plain markdown.
const INHOUSE_SYNTAX = /^\s*(table:|viz:)/im;

function locate(fileCardPrefix, citation) {
  const card = document.getElementById(`${fileCardPrefix}-${citation.attachmentId}`);
  card?.dispatchEvent(new CustomEvent("attachment-citation", {
    detail: {
      page: citation.page
    }
  }));
  card?.scrollIntoView?.({
    behavior: "smooth",
    block: "center"
  });
  card?.focus();
}

// Opens the cited file, at the cited page, in the page's right-hand viewer, the way the "more info:" buttons do.
// When this page holds no copy and Root can't send one (expired, removed), it scrolls to the file in the user's
// message instead.
async function showCitation(panel, fileCardPrefix, citation, name) {
  if (!(await openAttachment(panel, citation.attachmentId, name, citation.page ?? null))) locate(fileCardPrefix, citation);
}

// Looks like the "more info:" document buttons under a catalogue answer, so an attached file reads the same way.
const CHIP = "inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 hover:scale-105 bg-orange-500/90 dark:bg-orange-500/20 dark:hover:bg-orange-500/30 text-white dark:text-orange-300 border-orange-500/30";

const isHttps = source => !!source.url && /^https?:\/\//i.test(source.url);

// Our own documents (product datasheets, FAQ sources) open in the page's right-hand PDF panel through the shared
// ActionLinks, exactly like the "more info:" buttons under any other answer. Same URL fix-up extractActionLinks does.
const toPanelLink = source => ({
  title: source.title || source.id || source.url,
  url: source.url.replace("storage.cloud.google.com", "storage.googleapis.com")
});

function Footer({ label, ariaLabel, children }) {
  return <section aria-label={ariaLabel} className="mt-3 border-t-2 border-orange-500 py-3 dark:border-orange-500/20">
    <div className="pb-1 text-sm text-gray-500 dark:text-gray-400">{label}</div>
    <div className="flex flex-row flex-wrap gap-2">{children}</div>
  </section>;
}

export default function AttachmentAnswer({
  language,
  message,
  fileCardPrefix
}) {
  const en = language === "en";
  const panel = useContext(PdfPanelContext);
  const citations = (message.attachment_citations || []).filter(citation => {
    const file = message.files?.find(item => item.id === citation.attachmentId);
    if (!file || !/^a\d+$/.test(citation.citationId)) return false;
    return file.mimeType === "application/pdf" ? citation.page === null || (Number.isInteger(citation.page) && citation.page >= 1 && citation.page <= file.pageCount) : citation.page === null;
  });
  const text = message.text || "";
  const inHouse = INHOUSE_SYNTAX.test(text);
  const linked = text.replace(/\[(a\d+)\](?!\()/g, (marker, id) => citations.some(c => c.citationId === id) ? `[${id}](#attachment-${id})` : marker);
  // [label, ariaLabel, sources, opensInPanel]: web pages stay plain new-tab links (most sites refuse to be framed).
  const groups = [
    [en ? "Product database" : "產品 DB", "產品 DB", message.attachment_db_sources, true],
    [en ? "FAQ database" : "FAQ 資料庫", "FAQ 資料庫", message.attachment_faq_sources, true],
    [en ? "Web" : "網路", "網路", message.attachment_web_sources, false]
  ].filter(([, , sources]) => sources?.length);
  return <div>
    {inHouse ? <MessageRenderer text={text} language={language} hideActionLinks /> : <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
      img: ({
        alt
      }) => <span>{alt}</span>,
      a: ({
        href,
        children
      }) => {
        const citation = citations.find(c => href === `#attachment-${c.citationId}`);
        if (citation) return <button type="button" className="underline text-orange-700" onClick={() => showCitation(panel, fileCardPrefix, citation, message.files?.find(item => item.id === citation.attachmentId)?.name || citation.label)}>[{citation.citationId}]</button>;
        // Attachment prose never invents clickable URLs; only verified source lists do.
        return <span>{children}</span>;
      },
      p: ({
        children
      }) => <p className="my-2">{children}</p>
    }}>{linked}</ReactMarkdown>}
    {citations.length > 0 && <Footer label={en ? "Attached file:" : "附件："} ariaLabel="附件引用">
      {citations.map(citation => <button type="button" key={citation.citationId} className={CHIP} onClick={() => showCitation(panel, fileCardPrefix, citation, message.files?.find(item => item.id === citation.attachmentId)?.name || citation.label)}>
        {String(message.files?.find(item => item.id === citation.attachmentId)?.mimeType).startsWith("image/")
          ? <FileThumb id={citation.attachmentId} mimeType={message.files.find(item => item.id === citation.attachmentId).mimeType} size="h-6 w-6" />
          : <FiFileText className="h-4 w-4" aria-hidden="true" />}
        [{citation.citationId}] {message.files?.find(item => item.id === citation.attachmentId)?.name || citation.label}{citation.page != null ? (en ? `, p.${citation.page}` : `，第 ${citation.page} 頁`) : ""}
      </button>)}
    </Footer>}
    {groups.map(([label, ariaLabel, sources, opensInPanel]) => {
      const panelLinks = opensInPanel ? sources.filter(isHttps).map(toPanelLink) : [];
      const rest = opensInPanel ? sources.filter(source => !isHttps(source)) : sources;
      const summaryNote = sources.some(source => source.external_summary === true);
      return <Fragment key={ariaLabel}>
        {panelLinks.length > 0 && <ActionLinks links={panelLinks} label={`${label}${en ? ":" : "："}`} />}
        {(rest.length > 0 || (summaryNote && panelLinks.length === 0)) && <Footer label={label} ariaLabel={ariaLabel}>
          {rest.some(source => isHttps(source) && hasPreview(source)) ? <WebSourceCards sources={rest.filter(isHttps)} language={language} /> : rest.map((source, index) => isHttps(source)
            ? <a key={source.id || source.url || index} href={source.url} target="_blank" rel="noreferrer" className={CHIP}>{source.title || source.id || source.url}</a>
            : <span key={source.id || source.url || index} className="text-sm text-gray-700 dark:text-gray-200">{source.title || source.id || source.sourceKind}</span>)}
          {summaryNote && <span className="self-center text-xs text-gray-500">{en ? "(a summary, not the original page)" : "（摘要，非原頁面內容）"}</span>}
        </Footer>}
      </Fragment>;
    })}
    <AttachmentError error={message.attachment_error} language={language} />
    {!!message.attachment_data_gaps?.length && <section aria-label="資料缺口" className="mt-2 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
      <h3 className="font-semibold">{en ? "Not available" : "缺少的資料"}</h3>
      {message.attachment_data_gaps.map((gap, index) => <p key={index}>{typeof gap === "string" ? gap : JSON.stringify(gap)}</p>)}
    </section>}
  </div>;
}
